import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, Clock, GitMerge, XCircle, Users, Building2, Sparkles,
  AlertTriangle, CheckCircle, RefreshCw, Timer, Undo2, Play
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { formatDistanceToNow, format, differenceInHours } from 'date-fns';

interface PendingMerge {
  id: string;
  entity_type: string;
  primary_entity_id: string;
  merged_entity_id: string;
  primary_data: any;
  merged_data: any;
  match_score: number;
  rule_breakdown: Record<string, number>;
  status: 'pending' | 'executed' | 'revoked';
  scheduled_for: string;
  revoke_window_hours: number;
  created_at: string;
  executed_at: string | null;
  revoked_at: string | null;
}

const EntityIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'candidate': return <Users className="h-4 w-4 text-blue-500" />;
    case 'company': return <Building2 className="h-4 w-4 text-emerald-500" />;
    case 'skill': return <Sparkles className="h-4 w-4 text-violet-500" />;
    default: return <GitMerge className="h-4 w-4" />;
  }
};

const getTimeRemaining = (scheduledFor: string) => {
  const scheduled = new Date(scheduledFor);
  const now = new Date();
  const hoursRemaining = differenceInHours(scheduled, now);
  
  if (hoursRemaining < 0) return { text: 'Ready to execute', urgent: true };
  if (hoursRemaining < 2) return { text: `${hoursRemaining}h remaining`, urgent: true };
  return { text: `${hoursRemaining}h remaining`, urgent: false };
};

export const PendingMergesPanel = () => {
  const [selectedEntityType, setSelectedEntityType] = useState<string>('all');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const { data: pendingMerges, isLoading } = useQuery({
    queryKey: ['pending-auto-merges', selectedEntityType],
    queryFn: async () => {
      let query = supabase
        .from('pending_auto_merges')
        .select('*')
        .eq('status', 'pending')
        .order('scheduled_for', { ascending: true });

      if (selectedEntityType !== 'all') {
        query = query.eq('entity_type', selectedEntityType);
      }

      const { data, error } = await query.limit(100);
      if (error) throw error;
      return data as PendingMerge[];
    },
  });

  const { data: counts } = useQuery({
    queryKey: ['pending-merges-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pending_auto_merges')
        .select('entity_type, status');
      
      if (error) throw error;

      const result = {
        pending: { skill: 0, company: 0, candidate: 0, total: 0 },
        executed: 0,
        revoked: 0,
      };

      for (const item of data || []) {
        if (item.status === 'pending') {
          result.pending[item.entity_type as keyof typeof result.pending]++;
          result.pending.total++;
        } else if (item.status === 'executed') {
          result.executed++;
        } else if (item.status === 'revoked') {
          result.revoked++;
        }
      }

      return result;
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (mergeIds: string[]) => {
      const { error } = await supabase
        .from('pending_auto_merges')
        .update({ 
          status: 'revoked', 
          revoked_at: new Date().toISOString() 
        })
        .in('id', mergeIds);

      if (error) throw error;

      // Add to blocked pairs so they won't be suggested again
      const merges = pendingMerges?.filter(m => mergeIds.includes(m.id)) || [];
      for (const merge of merges) {
        // Use upsert-like behavior with select first
        const { data: existing } = await supabase
          .from('blocked_match_pairs')
          .select('id')
          .eq('entity_type', merge.entity_type)
          .eq('entity_a_id', merge.primary_entity_id)
          .eq('entity_b_id', merge.merged_entity_id)
          .limit(1);

        if (!existing?.length) {
          await supabase.from('blocked_match_pairs').insert({
            entity_type: merge.entity_type,
            entity_a_id: merge.primary_entity_id,
            entity_b_id: merge.merged_entity_id,
            blocked_reason: 'Revoked from pending auto-merge',
          });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-auto-merges'] });
      queryClient.invalidateQueries({ queryKey: ['pending-merges-counts'] });
      setSelectedItems(new Set());
      toast.success('Merge(s) revoked successfully');
    },
    onError: (error) => {
      toast.error('Failed to revoke: ' + error.message);
    },
  });

  const executeNowMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('execute-pending-merges', {
        body: { dry_run: false, limit: 100 },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pending-auto-merges'] });
      queryClient.invalidateQueries({ queryKey: ['pending-merges-counts'] });
      toast.success(`Executed ${data.executed} merges`);
    },
    onError: (error) => {
      toast.error('Failed to execute: ' + error.message);
    },
  });

  const toggleSelect = (id: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === (pendingMerges?.length || 0)) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(pendingMerges?.map(m => m.id) || []));
    }
  };

  const getEntityName = (merge: PendingMerge, isPrimary: boolean) => {
    const data = isPrimary ? merge.primary_data : merge.merged_data;
    if (!data) return 'Unknown';
    
    switch (merge.entity_type) {
      case 'candidate':
        return `${data.first_name || ''} ${data.last_name || ''}`.trim() || data.email_primary || 'Unknown';
      case 'company':
        return data.name || 'Unknown';
      case 'skill':
        return data.name || 'Unknown';
      default:
        return data.name || 'Unknown';
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Timer className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{counts?.pending.total || 0}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{counts?.executed || 0}</p>
                <p className="text-sm text-muted-foreground">Executed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              <div>
                <p className="text-2xl font-bold">{counts?.revoked || 0}</p>
                <p className="text-sm text-muted-foreground">Revoked</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">24h</p>
                <p className="text-sm text-muted-foreground">Revoke Window</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <GitMerge className="h-5 w-5" />
                Pending Auto-Merges
              </CardTitle>
              <CardDescription>
                Review and optionally revoke upcoming automatic merges before they execute
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedEntityType} onValueChange={setSelectedEntityType}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="skill">Skills ({counts?.pending.skill || 0})</SelectItem>
                  <SelectItem value="company">Companies ({counts?.pending.company || 0})</SelectItem>
                  <SelectItem value="candidate">Candidates ({counts?.pending.candidate || 0})</SelectItem>
                </SelectContent>
              </Select>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={selectedItems.size === 0 || revokeMutation.isPending}
                  >
                    {revokeMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Undo2 className="h-4 w-4 mr-2" />
                    )}
                    Revoke ({selectedItems.size})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Revoke Selected Merges?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will prevent {selectedItems.size} merge(s) from executing. 
                      The pairs will be added to the blocked list and won't be suggested again.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => revokeMutation.mutate(Array.from(selectedItems))}
                    >
                      Revoke
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <Button
                variant="outline"
                size="sm"
                onClick={() => executeNowMutation.mutate()}
                disabled={executeNowMutation.isPending || (counts?.pending.total || 0) === 0}
              >
                {executeNowMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Execute Ready
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !pendingMerges?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <GitMerge className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>No pending auto-merges</p>
              <p className="text-sm">High-confidence duplicates will appear here with a 24h revoke window</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {/* Header */}
                <div className="flex items-center gap-4 p-2 text-sm font-medium text-muted-foreground border-b">
                  <Checkbox
                    checked={selectedItems.size === pendingMerges.length && pendingMerges.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                  <div className="w-8">Type</div>
                  <div className="flex-1">Primary → Merged</div>
                  <div className="w-20 text-right">Score</div>
                  <div className="w-32 text-right">Executes In</div>
                </div>

                {pendingMerges.map((merge) => {
                  const timeInfo = getTimeRemaining(merge.scheduled_for);
                  return (
                    <div
                      key={merge.id}
                      className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
                        selectedItems.has(merge.id) ? 'bg-accent border-primary' : 'hover:bg-accent/50'
                      }`}
                    >
                      <Checkbox
                        checked={selectedItems.has(merge.id)}
                        onCheckedChange={() => toggleSelect(merge.id)}
                      />
                      <div className="w-8">
                        <EntityIcon type={merge.entity_type} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">
                            {getEntityName(merge, true)}
                          </span>
                          <span className="text-muted-foreground">←</span>
                          <span className="truncate text-muted-foreground">
                            {getEntityName(merge, false)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Created {formatDistanceToNow(new Date(merge.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      <div className="w-20 text-right">
                        <Badge variant={merge.match_score >= 0.95 ? 'default' : 'secondary'}>
                          {(merge.match_score * 100).toFixed(0)}%
                        </Badge>
                      </div>
                      <div className="w-32 text-right">
                        <Badge variant={timeInfo.urgent ? 'destructive' : 'outline'}>
                          {timeInfo.urgent && <AlertTriangle className="h-3 w-3 mr-1" />}
                          {timeInfo.text}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
